import { useCallback, useEffect, useState } from "react";
import { useUrlState } from "@/lib/navState";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Plate, StatusBadge } from "@/components/StatusBadge";
import { formatDate, formatRupiah, label } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Search, ArrowRight, Car, ChevronRight, ArrowLeft } from "lucide-react";

export default function History() {
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [visits, setVisits] = useState([]);
  const [visit, setVisit] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [search, setSearch] = useUrlState("q", "");
  const navigate = useNavigate();

  const loadVehicles = useCallback(async (q = "") => {
    const { data } = await api.get("/vehicles", { params: { q } });
    setVehicles(data);
  }, []);
  useEffect(() => { loadVehicles(); }, [loadVehicles]);
  useEffect(() => { const t = setTimeout(() => loadVehicles(search), 300); return () => clearTimeout(t); }, [search, loadVehicles]);

  const openVehicle = useCallback(async (vid) => {
    setVisit(null); setTimeline([]);
    const { data } = await api.get(`/vehicles/${vid}/claims`);
    setVehicle(data.vehicle); setVisits(data.visits);
  }, []);

  // deep-link ?vehicle=VEH-xxxx
  useEffect(() => { const v = searchParams.get("vehicle"); if (v) openVehicle(v); }, [searchParams, openVehicle]);

  const openVisit = async (v) => {
    setVisit(v);
    const { data } = await api.get(`/claims/${v.id}/history`);
    setTimeline((data || []).slice().reverse());
  };

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-[#111827] sm:text-3xl">History Kendaraan</h1>
      <p className="mt-1 text-sm text-gray-500">Cari Nopol/VIN → lihat seluruh kunjungan (Level 1). Klik satu Claim/WO untuk timeline detail (Level 2).</p>

      {!vehicle ? (
        <>
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="history-search-input" placeholder="Cari Nopol, VIN, Vehicle ID, atau pemilik…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-12 pl-10 text-base" />
          </div>
          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {vehicles.map((v) => (
              <button key={v.vehicle_id} data-testid={`history-vehicle-${v.vehicle_id}`} onClick={() => openVehicle(v.vehicle_id)} className="flex items-center justify-between rounded-lg border border-gray-100 bg-white p-4 text-left shadow-sm transition-colors hover:border-[#3B82F6]">
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><Plate value={v.license_plate} /><span className="font-mono text-[10px] font-bold text-gray-500">{v.vehicle_id}</span></div>
                  <div className="mt-1 truncate text-sm text-gray-700">{[v.brand, v.model].filter(Boolean).join(" ") || "—"}</div>
                  <div className="mt-0.5 text-xs text-gray-500">{v.visit_count} visit · {v.active_count} aktif{v.needs_review ? " · ⚠ Needs Review" : ""}</div>
                </div>
                <ChevronRight size={18} className="shrink-0 text-gray-400" />
              </button>
            ))}
            {vehicles.length === 0 && <p className="text-sm text-gray-400">Tidak ada kendaraan.</p>}
          </div>
        </>
      ) : (
        <div className="mt-6">
          <button data-testid="history-back-btn" onClick={() => { setVehicle(null); setVisits([]); setVisit(null); setTimeline([]); }} className="mb-3 flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-[#111827]"><ArrowLeft size={16} /> Semua Kendaraan</button>

          {/* Vehicle profile */}
          <div className="rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-3">
              <Car size={22} className="text-[#3B82F6]" />
              <Plate value={vehicle.license_plate} />
              <span className="font-mono text-xs font-bold text-gray-500">{vehicle.vehicle_id}</span>
              <span className="text-sm font-semibold text-gray-800">{[vehicle.brand, vehicle.model].filter(Boolean).join(" ")}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-500 sm:grid-cols-4">
              <div>VIN: <b className="text-gray-700">{vehicle.vin || "—"}</b></div>
              <div>No Mesin: <b className="text-gray-700">{vehicle.engine_no || "—"}</b></div>
              <div>Pemilik: <b className="text-gray-700">{vehicle.owner_name || "—"}</b></div>
              <div>HP: <b className="text-gray-700">{vehicle.phone || "—"}</b></div>
            </div>
          </div>

          {/* Level 1 — visits */}
          <h2 className="mt-6 font-heading text-lg font-semibold text-[#111827]">Repair / Claim History <span className="text-sm font-normal text-gray-400">({visits.length} visit)</span></h2>
          <div className="mt-3 space-y-2">
            {visits.map((v) => (
              <button key={v.id} data-testid={`history-visit-${v.id}`} onClick={() => openVisit(v)} className={`flex w-full items-center gap-3 rounded-lg border bg-white p-3 text-left shadow-sm transition-colors ${visit?.id === v.id ? "border-[#3B82F6]" : "border-gray-100 hover:border-gray-300"}`}>
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#111827] text-xs font-bold text-white">#{v.visit}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-mono font-bold text-[#8a6d1a]">{v.claim_no}</span>
                    <span className="font-mono text-xs text-gray-500">WO {v.wo_number}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-gray-500">{formatDate(v.date)} · {v.insurance} · {formatRupiah(v.grand_total)}</div>
                </div>
                <StatusBadge status={v.status} />
              </button>
            ))}
            {visits.length === 0 && <p className="text-sm text-gray-400">Belum ada kunjungan.</p>}
          </div>

          {/* Level 2 — timeline for selected visit */}
          {visit && (
            <div className="mt-6 rounded-lg border border-gray-100 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-800">Timeline · {visit.claim_no} <span className="text-gray-400">/ WO {visit.wo_number}</span></div>
                <button data-testid="open-claim-btn" onClick={() => navigate(`/claims/${visit.id}`)} className="text-sm text-[#3B82F6] hover:underline">Buka Claim</button>
              </div>
              <div className="relative pl-6">
                <div className="absolute bottom-1 left-1.5 top-1 w-px bg-gray-200" />
                {timeline.map((h) => (
                  <div key={h.id} data-testid={`history-timeline-${h.id}`} className="relative mb-5 last:mb-0">
                    <div className="absolute -left-[18px] top-1 h-3 w-3 rounded-full border-2 border-[#3B82F6] bg-white" />
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      {h.previous_status && <StatusBadge status={h.previous_status} />}
                      {h.previous_status && <ArrowRight size={13} className="text-gray-400" />}
                      <StatusBadge status={h.new_status} />
                    </div>
                    <div className="mt-1 text-xs text-gray-500">{formatDate(h.date)} {h.time} · {h.user}{h.module ? ` · ${label(h.module)}` : ""}{h.notes ? ` · ${h.notes}` : ""}</div>
                  </div>
                ))}
                {timeline.length === 0 && <p className="text-sm text-gray-400">Belum ada riwayat.</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
